import type {
  ProductCreateRequest,
  ProductDetail,
  ProductUpdateRequest,
} from '@/lib/api-admin/types';
import {
  BENEFIT_EXCLUSIONS,
  PRODUCT_LOCALES,
  type BenefitExclusion,
  type CurrencyPrices,
  type ProductFormValues,
  type ProductTranslations,
  type ShippingFeeType,
} from '../schemas/productSchema';
import { buildComboPreviewPairs, comboKeyOf } from './variantPartition';
import { commonConsentCodesFromDetail } from '../commonConsents/commonConsents';

/** QA-456 2단계 — 마일리지 "지급 안 함"(고도몰에서 아무에게도 적립하지 않던 상품) 특수값. */
const MILEAGE_NONE = 'NONE';
/** QA-579 마일리지 사용 제외 — 전체 회원 제외 표시값(등급 id 대신). */
const MILEAGE_USE_EXCLUDE_ALL = 'ALL';

const VALID_SHIPPING_FEE_TYPES = new Set<ShippingFeeType>([
  'FREE',
  'FLAT',
  'CONDITIONAL',
  'REGION',
]);

/**
 * mall-visibility 파트1 — 상품 노출 국가(몰) 와이어 변환 (be-cat V116 계약 정합).
 *
 * 폼은 "노출 대상 몰" 코드 집합(visibleMalls)을 보유한다. 와이어 필드 = `visibleMallCodes`(string[]).
 * be-cat 계약: CREATE 누락/빈=전체노출 · PUT **`[]`=전체노출(제한 해제)** / null=미변경 / 값=그 몰만.
 * 폼은 항상 명시 상태(미변경 의도 없음)이므로 **전체노출(4몰 전부 선택)=빈 배열 `[]` 전송**(제한 해제,
 * CREATE·PUT 양쪽 전체노출 시맨틱과 정합). 일부 선택만 표준 순서로 정규화해 전송(byte 안정).
 * 서버가 미지원/중복/blank 정규화하므로 FE 는 4개 몰 코드만 보낸다.
 *
 * ★be-cat 계약 의존점: 와이어 필드명(`VISIBLE_MALLS_WIRE_KEY`)과 전체노출 표현은 이 한 곳만 조정.
 */
const VISIBLE_MALLS_WIRE_KEY = 'visibleMallCodes' as const;

const buildVisibleMallsPayload = (
  malls: ReadonlyArray<string> | undefined,
): string[] => {
  if (!malls) return []; // 미설정 = 전체노출(빈 배열 = 제한 해제).
  const selected = PRODUCT_LOCALES.filter((m) => malls.includes(m));
  // 전체노출(4몰 전부) = 빈 배열(제한 해제). 일부 선택만 명시 전송.
  if (selected.length === 0 || selected.length === PRODUCT_LOCALES.length) return [];
  return [...selected];
};

/**
 * 편집 로드 시 노출 국가(몰) 시드. BE 응답 `commerceMeta.visibleMallCodes`(string[])에 부분 노출
 * 배열이 있으면 그 집합으로, 없거나 비었거나 4몰 전부면 **전체노출**(전 몰 선택)로 복원. 폼은 항상
 * "노출 대상 몰" 집합을 보유(전체노출=4몰 전부 선택)하므로 toggle UI 가 일관되게 동작한다.
 */
const visibleMallsFromDetail = (
  raw: ReadonlyArray<string> | null | undefined,
): (typeof PRODUCT_LOCALES)[number][] => {
  if (!raw || raw.length === 0) return [...PRODUCT_LOCALES]; // 전체노출 기본.
  const selected = PRODUCT_LOCALES.filter((m) => raw.includes(m));
  return selected.length === 0 ? [...PRODUCT_LOCALES] : selected;
};

interface ShippingWire {
  feeType?: string | null;
  baseFee?: number | null;
  islandExtraFee?: number | null;
  freeThreshold?: number | null;
  returnFee?: number | null;
  exchangeFee?: number | null;
}

interface PolicyLinksWire {
  shippingPolicyId?: number | null;
  guidePolicyId?: number | null;
  operationPolicyId?: number | null;
}

/** R5 A-2 응답 commerceMeta 객체 (ProductDetailView.commerceMeta). presaleStatus 는 enum 이름 string. */
interface CommerceMetaWire {
  taxExempt?: boolean | null;
  searchKeywords?: string[] | null;
  sortWeight?: number | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  additionalCategoryIds?: string[] | null;
  relatedProductIds?: string[] | null;
  presaleStatus?: string | null;
  // ID39 노출 예약 window — 응답은 commerceMeta trailing (CommerceMetaView).
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  // F-1 — BE 가 b2bOnly 를 commerceMeta 로 내려줄 경우 대비(top-level 우선, 없으면 여기 폴백).
  b2bOnly?: boolean | null;
  // mall-visibility — 노출 국가몰 코드 배열(be-cat V116 정본 위치 = commerceMeta.visibleMallCodes).
  visibleMallCodes?: string[] | null;
  // QA-507 공통 동의 사용 코드(catalog QA-479 — 없으면 []).
  commonConsentCodes?: string[] | null;
  // V130 수동 품절(고도몰 품절 표시). 이 필드가 없는 응답(BE 반영 전)이면 폼에 넣지 않는다.
  manualSoldOut?: boolean | null;
  // V131 구매·접근 권한(상세 commerceMeta). 응답에 없으면(BE 반영 전) 기본값(전체 허용)으로 복원.
  purchasePermission?: string | null;
  purchaseDeniedText?: string | null;
  accessPermission?: string | null;
  accessDeniedDisplay?: boolean | null;
  // V132 특정회원등급 — permission=GRADE 일 때만 값(그 외 빈 목록).
  purchaseGradeIds?: string[] | null;
  accessGradeIds?: string[] | null;
  // QA-456 혜택 제외 — 없으면 빈 목록(BE). 필드 자체가 없으면 BE 반영 전.
  benefitExclusions?: string[] | null;
  benefitExclusionGradeIds?: string[] | null;
  // QA-456 2단계 마일리지 지급 대상 — [] = 전체회원, 등급 목록, ["NONE"] = 지급 안 함. 필드가 없으면 BE 반영 전.
  mileageGradeIds?: string[] | null;
  // QA-579 마일리지 사용 제외 — [] = 제외 없음, ["ALL"] = 전체 회원, 등급 목록 = 그 등급만. 필드가 없으면 BE 반영 전.
  mileageUseExclusionGradeIds?: string[] | null;
  // M-8 ID81 배송비 마스터(fee-policies) 연결 — CommerceMetaView round-trip(VARCHAR36).
  shippingFeePolicyId?: string | null;
  // ① Task#7 — 실측 무게(g, V107). CommerceMetaView trailing round-trip. null = 미설정.
  weightGrams?: number | null;
  // 등록일/수정일(읽기 전용, catalog fceac51). 폼 값이 아니라 표시만 한다(productAuditTimes).
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ProductAuditTimes {
  createdAt: string | null;
  updatedAt: string | null;
  /** QA-515 등록자·수정자(관리자 ID). BE 반영 전·이관 상품은 null. */
  createdBy: string | null;
  updatedBy: string | null;
}

/** 상세 기본 정보의 등록일/수정일·등록자/수정자 — commerceMeta 에서 읽는다. 저장 페이로드에는 넣지 않는다. */
export const productAuditTimes = (detail: ProductDetail): ProductAuditTimes => {
  const meta = (detail as unknown as { commerceMeta?: (CommerceMetaWire & { createdBy?: string | null; updatedBy?: string | null }) | null })
    .commerceMeta;
  return {
    createdAt: meta?.createdAt ?? null,
    updatedAt: meta?.updatedAt ?? null,
    createdBy: meta?.createdBy ?? null,
    updatedBy: meta?.updatedBy ?? null,
  };
};

/** 구매·접근 권한 응답값 → 폼 값. 모르는 값·없음은 ALL(전체 허용). */
const toMemberPermission = (v: string | null | undefined): 'ALL' | 'MEMBER' | 'GRADE' =>
  v === 'MEMBER' || v === 'GRADE' ? v : 'ALL';

const VALID_PRESALE_STATUSES = new Set(['PRESALE_SCHEDULED', 'PRESALE_OPEN']);
const toPresaleStatus = (v: string | null | undefined): 'PRESALE_SCHEDULED' | 'PRESALE_OPEN' | null =>
  v && VALID_PRESALE_STATUSES.has(v) ? (v as 'PRESALE_SCHEDULED' | 'PRESALE_OPEN') : null;

const toShippingFeeType = (value: string | null | undefined): ShippingFeeType | null =>
  value && VALID_SHIPPING_FEE_TYPES.has(value as ShippingFeeType)
    ? (value as ShippingFeeType)
    : null;

/**
 * datetime-local('YYYY-MM-DDTHH:mm') / 풀 ISO / '' → BE Instant(ISO offset, 'Z') | undefined.
 * BE salesStartAt/salesEndAt/shippingStartAt 는 Instant 라 'Z'/offset 이 없으면 Jackson 파싱 실패.
 * 빈 값/파싱 불가 시 undefined → 페이로드에서 생략.
 */
const toInstant = (v: string | null | undefined): string | undefined => {
  if (v == null || v === '') return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

/** type=date 'YYYY-MM-DD' / '' → LocalDate | undefined (빈 값은 미전송). */
const toLocalDate = (v: string | null | undefined): string | undefined =>
  v && v !== '' ? v : undefined;

/**
 * 가격 UI 는 `prices.KRW.listAmount/saleAmount` 만 바인딩하고 listPrice/salePrice 입력은 없다.
 * → BE 대표가격(listPrice/salePrice, KRW 컬럼)을 prices.KRW 에서 도출해 ₩0 저장을 막는다.
 * prices 가 없으면(레거시) 폼의 listPrice/salePrice 로 폴백.
 */
const krwMoney = (values: ProductFormValues, kind: 'list' | 'sale') => {
  const krw = values.prices?.KRW;
  const amount = krw
    ? kind === 'list'
      ? krw.listAmount
      : krw.saleAmount
    : kind === 'list'
      ? values.listPrice.amount
      : values.salePrice.amount;
  return { amount, currencyCode: 'KRW' };
};

/**
 * 정본(ko) 상세 본문 일원화 — 상품 상세 본문은 §2 다국어 탭의 한국어 에디터
 * (translations['ko-KR'].detailHtml) 가 **유일한** 입력 소스다(§5 단독 에디터 제거).
 * BE products.detail_html(top-level)은 이 ko 정본을 미러링해야 SF 한국어 노출과 BE 대표
 * 컬럼이 항상 일치한다. 편집 로드 시 translationsFromDetail 이 레거시 top-level 값을 ko 로
 * 시드하므로, 여기서는 ko 만 읽는다(레거시 top-level 폴백 X — 그래야 ko 를 비우면 BE 도
 * 비워져 clear 가 정상 동작한다). 비어 있으면 undefined → 페이로드에서 생략.
 */
const canonicalDetailHtml = (values: ProductFormValues): string | undefined =>
  values.translations?.['ko-KR']?.detailHtml?.trim() || undefined;

/**
 * D1/D3 정본 일원화 — ko 상품명은 §1 기본정보(nameKo), ko 고시 항목은 §11 템플릿 섹션(noticeSpec)이
 * 유일한 입력 소스다(§2 ko 탭의 중복 입력 제거). wire 의 translations['ko-KR'].{name,noticeSpec} 는
 * 여기서 그 정본을 미러링해 top-level 컬럼과 번역 컬럼이 한 PUT 안에서 절대 어긋나지 않게 한다.
 * 비어 있으면 cleanTranslations 가 정리하므로 그대로 둔다(clear 시 둘 다 비워짐).
 */
const withKoCanonicals = (values: ProductFormValues): ProductTranslations => {
  const base = values.translations ?? {};
  const ko = base['ko-KR'] ?? {};
  return {
    ...base,
    'ko-KR': {
      ...ko,
      name: values.nameKo,
      noticeSpec: values.noticeSpec ?? {},
    },
  };
};

const omitUndefined = <T extends Record<string, unknown>>(obj: T): T => {
  const out = {} as Record<string, unknown>;
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as T;
};

const cleanTranslations = (t: ProductTranslations | undefined): Record<string, unknown> | undefined => {
  if (!t) return undefined;
  const out: Record<string, unknown> = {};
  for (const loc of PRODUCT_LOCALES) {
    const entry = t[loc];
    if (!entry) continue;
    // ID42: localized notice_spec — 빈 키/값 제거 후 비어있으면 생략.
    const noticeSpec = entry.noticeSpec ? cleanNoticeSpec(entry.noticeSpec) : undefined;
    // ID42: 옵션명 번역 — name 비어있는 옵션 제외, choices 라벨도 비면 제외, 전부 비면 생략.
    const options = entry.options
      ?.map((opt) => {
        const name = opt.name?.trim();
        const choices = opt.choices
          ?.map((c) => ({ choiceId: c.choiceId, label: c.label?.trim() ?? '' }))
          .filter((c) => c.label.length > 0);
        return omitUndefined({
          optionId: opt.optionId,
          name: name || undefined,
          choices: choices && choices.length > 0 ? choices : undefined,
        });
      })
      .filter((opt) => opt.name !== undefined || opt.choices !== undefined);
    // ID42 #14: variant 라벨 번역 — label 비어있는 variant 제외, 전부 비면 생략.
    const variants = entry.variants
      ?.map((v) => ({ variantCode: v.variantCode, label: v.label?.trim() ?? '' }))
      .filter((v) => v.variantCode && v.label.length > 0);
    const trimmed = omitUndefined({
      name: entry.name?.trim() || undefined,
      description: entry.description?.trim() || undefined,
      brandName: entry.brandName?.trim() || undefined,
      eventPromoSubtitle: entry.eventPromoSubtitle?.trim() || undefined,
      // ID42 전필드 다국어 — detailHtml / noticeText / policyText / shippingPolicyText / noticeSpec / options / variants.
      detailHtml: entry.detailHtml?.trim() || undefined,
      noticeText: entry.noticeText?.trim() || undefined,
      policyText: entry.policyText?.trim() || undefined,
      shippingPolicyText: entry.shippingPolicyText?.trim() || undefined,
      noticeSpec,
      options: options && options.length > 0 ? options : undefined,
      variants: variants && variants.length > 0 ? variants : undefined,
    });
    if (Object.keys(trimmed).length > 0) {
      out[loc] = trimmed;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

const cleanNoticeSpec = (spec: Record<string, string> | undefined): Record<string, string> | undefined => {
  if (!spec) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(spec)) {
    const key = k.trim();
    const value = v?.trim();
    if (key && value) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

const cleanImages = (imgs: string[] | undefined): string[] | undefined => {
  if (!imgs) return undefined;
  const filtered = imgs.map((s) => s.trim()).filter((s) => s.length > 0);
  return filtered.length > 0 ? filtered : undefined;
};

/**
 * 가격 맵 정리. KRW 기준가는 항상 전송. 외화(USD/JPY/CNY)는 선택적 오버라이드라
 * listAmount/saleAmount 둘 다 null 이면 해당 통화를 통째로 생략(BE 가 환율로 자동계산),
 * 한쪽만 입력되면 입력된 값만 보낸다. prices 자체가 비면 undefined.
 */
const cleanPrices = (
  prices: CurrencyPrices | undefined,
): Record<string, Record<string, number>> | undefined => {
  if (!prices) return undefined;
  const out: Record<string, Record<string, number>> = {
    KRW: { listAmount: prices.KRW.listAmount, saleAmount: prices.KRW.saleAmount },
  };
  for (const currency of ['USD', 'JPY', 'CNY'] as const) {
    const entry = prices[currency];
    const override: Record<string, number> = {};
    if (entry.listAmount != null) override.listAmount = entry.listAmount;
    if (entry.saleAmount != null) override.saleAmount = entry.saleAmount;
    if (Object.keys(override).length > 0) out[currency] = override;
  }
  return out;
};

const buildCommonPayload = (values: ProductFormValues) =>
  omitUndefined({
    productType: values.productType,
    status: values.status,
    // 노출여부(전시 on/off) — 상품상태와 독립. PUT 시 null=ON 시맨틱이라 항상 boolean 전송.
    // QA-606 — 상품 상태 [비공개](HIDDEN)는 노출 끔이다. BE 는 status 적용 뒤 displayed 로 덮어써서, 체크박스 값(true)을
    //   그대로 보내면 비공개가 풀렸다 → HIDDEN 이면 displayed=false.
    displayed: values.status === 'HIDDEN' ? false : (values.displayed ?? true),
    // F-1 B2B 전용 — top-level boolean(products.b2b_only). 미설정=false 명시 전송(displayed 패턴).
    b2bOnly: values.b2bOnly ?? false,
    // V131 구매·접근 권한 — 4개를 항상 함께 보낸다(PUT null = 미변경이라 폼 값을 명시). 문구는 빈 값이면 지움.
    purchasePermission: values.purchasePermission ?? 'ALL',
    // V132 — GRADE 일 때만 고른 등급, 그 외엔 빈 목록(전환 시 이전 등급이 남지 않게).
    purchaseGradeIds: values.purchasePermission === 'GRADE' ? (values.purchaseGradeIds ?? []) : [],
    // 가격 대체 문구 — 구매 권한이 전체가 아니고 "사용" 체크일 때만 문구, 그 외엔 "" (BE: 빈 문자열 = 지움).
    //   전체로 되돌리면 문구도 비워진다(고도몰 저장 규칙).
    purchaseDeniedText:
      (values.purchasePermission ?? 'ALL') !== 'ALL' && values.purchaseDeniedTextEnabled
        ? (values.purchaseDeniedText?.trim() ?? '')
        : '',
    accessPermission: values.accessPermission ?? 'ALL',
    accessGradeIds: values.accessPermission === 'GRADE' ? (values.accessGradeIds ?? []) : [],
    accessDeniedDisplay: values.accessDeniedDisplay ?? true,
    // QA-456 혜택 제외 — 폼에 값이 있을 때만(상세에 필드가 없던 화면은 보내지 않아 기존 값 유지). 혜택을 모두 풀면 대상 등급도 [].
    ...(values.benefitExclusions !== undefined
      ? {
          benefitExclusions: values.benefitExclusions,
          benefitExclusionGradeIds:
            values.benefitExclusions.length > 0 && values.benefitExclusionTarget === 'GRADE'
              ? (values.benefitExclusionGradeIds ?? [])
              : [],
        }
      : {}),
    // QA-456 2단계 마일리지 지급 대상 — 폼에 값이 있을 때만(상세에 필드가 없던 폼은 보내지 않아 기존 값 유지).
    ...(values.mileageTarget !== undefined
      ? {
          mileageGradeIds:
            values.mileageTarget === 'NONE'
              ? [MILEAGE_NONE]
              : values.mileageTarget === 'GRADE'
                ? (values.mileageGradeIds ?? []).filter((id) => id !== MILEAGE_NONE)
                : [],
        }
      : {}),
    // QA-579 마일리지 사용 제외 — 폼에 값이 있을 때만(상세에 필드가 없던 폼은 보내지 않아 기존 값 유지).
    ...(values.mileageUseExclusionTarget !== undefined
      ? {
          mileageUseExclusionGradeIds:
            values.mileageUseExclusionTarget === 'ALL'
              ? [MILEAGE_USE_EXCLUDE_ALL]
              : values.mileageUseExclusionTarget === 'GRADE'
                ? (values.mileageUseExclusionGradeIds ?? []).filter((id) => id !== MILEAGE_USE_EXCLUDE_ALL)
                : [],
        }
      : {}),
    // ID39 노출 예약 window. 명시적 null 전송(omit 아님) — "예약 해제/종료일 비움(상시노출)"
    // 의도가 BE 에 전달돼야 한다. 빈 값이면 toInstant 가 undefined → null 로 강제.
    displayStartAt: toInstant(values.displayStartAt) ?? null,
    displayEndAt: toInstant(values.displayEndAt) ?? null,
    primaryCategoryId: values.primaryCategoryId,
    additionalCategoryIds: values.additionalCategoryIds && values.additionalCategoryIds.length > 0
      ? values.additionalCategoryIds
      : undefined,
    // mall-visibility — visibleMallCodes 항상 명시(전체노출=빈 배열 제한해제, 일부=그 몰만). PUT null=미변경
    // 시맨틱이나 폼은 항상 명시 상태라 빈 배열로 전체노출을 표현(be-cat V116 정합).
    [VISIBLE_MALLS_WIRE_KEY]: buildVisibleMallsPayload(values.visibleMalls),
    // QA-507 공통 동의 — 폼은 항상 명시 상태라 그대로 보낸다([] = 모두 사용 안 함). 값이 없으면(폼 밖 호출) 생략 = 그대로.
    ...(values.commonConsentCodes !== undefined ? { commonConsentCodes: [...values.commonConsentCodes] } : {}),
    brandId: values.brandId ?? undefined,
    nameKo: values.nameKo,
    // D4 — nameEn 은 입력란이 없는 숨은 중복 필드였다(편집 로드값 stale round-trip).
    // §2 영어 탭의 상품명(translations['en-US'].name)에서 도출해 두 wire 필드가 항상 일치하게 한다.
    nameEn: values.translations?.['en-US']?.name?.trim() || undefined,
    artistName: values.artistName ?? undefined,
    artistId: values.artistId ?? undefined,
    artistMemberIds:
      values.artistMemberIds && values.artistMemberIds.length > 0
        ? values.artistMemberIds
        : undefined,
    // QA-231 — productCode 는 서버 자동배정 고유값. 등록 시에는 전송을 생략(서버 배정),
    // 수정 시에는 기존 값을 라운드트립한다(값이 없으면 omit). 빈 문자열/ null 은 보내지 않는다.
    productCode: values.productCode?.trim() || undefined,
    // QA-231 자체상품코드(앨범별 고정 코드, 선택). 비우면 omit. trim 후 빈 값이면 미전송.
    selfProductCode: values.selfProductCode?.trim() || undefined,
    barcode: values.barcode ?? undefined,
    originCountry: values.originCountry ?? undefined,
    manufacturerName: values.manufacturerName ?? undefined,
    manufacturedYearMonth: values.manufacturedYearMonth ?? undefined,
    listPrice: krwMoney(values, 'list'),
    salePrice: krwMoney(values, 'sale'),
    prices: cleanPrices(values.prices),
    // QA-620 — 비운 판매 기간은 null 로 명시(상시 판매). 빼면 BE 가 기존 기간을 남긴다(복사한 상품에서 못 지움).
    salesStartAt: toInstant(values.salesStartAt) ?? null,
    salesEndAt: toInstant(values.salesEndAt) ?? null,
    // QA-490 — 비운 발매일은 null 로 명시(삭제 의도). BE PUT 은 요청값으로 덮어쓰므로(catalog PR #88) 필드를 빼면 안 된다.
    releaseDate: toLocalDate(values.releaseDate) ?? null,
    stockInboundDate: toLocalDate(values.stockInboundDate),
    shippingStartAt: toInstant(values.shippingStartAt),
    // 명시적 null 전송(omit 아님) — "구매수량 제한 해제" 의도가 BE 에 전달돼야 한다.
    // (BE writer 가 null patch-skip 이면 clear 가 안 되는 버그 → be-catalog 보고. SET 은 정상 round-trip.)
    purchaseLimitMin: values.purchaseLimitMin ?? null,
    purchaseLimitMax: values.purchaseLimitMax ?? null,
    // QA-542 구매수량 기준 — 고른 값만 보낸다(등록 때 비우면 BE 가 OPTION, 수정 때 비우면 그대로).
    ...(values.purchaseLimitBasis ? { purchaseLimitBasis: values.purchaseLimitBasis } : {}),
    // 다국어 — ko 정본(nameKo/§11 noticeSpec)을 ko 번역에 미러링한 뒤 정리(D1/D3).
    translations: cleanTranslations(withKoCanonicals(values)),
    // R6 E — 제목 하단 '간단 설명' top-level(products.description). 입력란은 §2 한국어 탭의
    // description 하나뿐 → ko 정본을 top-level 로 승격(detailHtml/eventPromoSubtitle 동일 패턴).
    // per-locale override(translations[loc].description)는 cleanTranslations 가 그대로 보존.
    // QA-490 — 비우면 빈 문자열을 보내 지운다(BE: null=미변경, ''=비우기). omit 하면 이전 값이 FE 에 남는다. generated 타입 codegen-lag → 객체리터럴은
    // as-cast 로 통과(상위 함수에서 ProductCreateRequest/UpdateRequest 로 캐스팅).
    description: values.translations?.['ko-KR']?.description?.trim() ?? '',
    // 상세 본문 + 이미지 — top-level detailHtml 은 ko 정본(translations['ko-KR'].detailHtml)을
    // 미러링한다(정본 일원화). 입력란은 §2 다국어 탭의 한국어 에디터 하나뿐.
    thumbnailUrl: values.thumbnailUrl?.trim() || undefined,
    detailImages: cleanImages(values.detailImages),
    detailHtml: canonicalDetailHtml(values),
    // 배송
    shippingFree: values.shippingFree,
    wishlistEnabled: values.wishlistEnabled,
    // 배송비 (shippingFeeType 은 항상 전송 — 아래 update/create 에서 별도 보강)
    baseFee: values.baseFee ?? undefined,
    islandExtraFee: values.islandExtraFee ?? undefined,
    freeThreshold: values.freeThreshold ?? undefined,
    returnFee: values.returnFee ?? undefined,
    exchangeFee: values.exchangeFee ?? undefined,
    // M-8 ID81 배송비 마스터 연결(null = 미지정 → 기본 템플릿 자동 적용).
    // shippingPolicyId(Long, 배송 안내 텍스트정책)와 다른 필드.
    shippingFeePolicyId: values.shippingFeePolicyId ?? null,
    // ① Task#7 — 실측 무게(g). 미설정(null)은 omit(BE null=미변경 보존). clear(0=미설정 복귀)는
    // withExplicitClears 가 baseline 인지로 주입(편집에서 로드된 실값을 비운 경우만).
    weightGrams: values.weightGrams ?? undefined,
    // 정책 연결 (null = 연결 해제 → 기본정책 fallback)
    shippingPolicyId: values.shippingPolicyId ?? null,
    guidePolicyId: values.guidePolicyId ?? null,
    operationPolicyId: values.operationPolicyId ?? null,
    // 이벤트 — D5: top-level 부제는 입력란이 없는 stale round-trip 이었다. 실제 입력 소스인
    // §2 ko 탭(translations['ko-KR'].eventPromoSubtitle)에서 도출(편집 로드 시 레거시 top-level 값은
    // translationsFromDetail 이 ko 로 시드하므로 무손실).
    eventPromoSubtitle: values.translations?.['ko-KR']?.eventPromoSubtitle?.trim() || undefined,
    // 고시
    noticeSpec: cleanNoticeSpec(values.noticeSpec),
    // R5 A-2 commerceMeta (요청은 flat top-level). 부가세는 항상 boolean 전송(과세/면세 명시),
    // 나머지는 입력값 그대로(배열은 빈배열=clear, presaleStatus 는 null=일반판매 시맨틱).
    taxExempt: values.taxExempt ?? false,
    searchKeywords: values.searchKeywords ?? undefined,
    sortWeight: values.sortWeight ?? undefined,
    seoTitle: values.seoTitle?.trim() || undefined,
    seoDescription: values.seoDescription?.trim() || undefined,
    relatedProductIds: values.relatedProductIds ?? undefined,
    presaleStatus: values.presaleStatus ?? null,
  });

/**
 * #10c/결정④ — optionGroups wire 페이로드(ProductOptionGroupDto 정합). create/update 공용.
 * groupId/choiceId 는 GET option-groups 응답에서 시드된 reconcile 매칭 키(선택) — wire 의
 * choice id 필드명은 BE DTO 대로 `groupChoiceId`. id 가 있으면 ID-우선 매칭(rename-safe),
 * 없으면(신규 행/등록) 이름·라벨 매칭 폴백. 등록 경로엔 id 가 없어 자연히 종전과 동일 페이로드.
 */
const mapOptionGroupsPayload = (groups: ReadonlyArray<NonNullable<ProductFormValues['optionGroups']>[number]>) =>
  groups.map((g, gi) =>
    omitUndefined({
      groupId: g.groupId ?? undefined,
      name: g.name,
      required: g.required ?? undefined,
      position: g.position ?? gi,
      choices: g.choices.map((c, ci) =>
        omitUndefined({
          groupChoiceId: c.choiceId ?? undefined,
          label: c.label,
          additionalAmount: c.additionalAmount ?? undefined,
          position: c.position ?? ci,
        }),
      ),
    }),
  );

/**
 * QA-R6 G(옵션 A) — 등록 시 조합별 재고 페이로드. 조합 variant 는 BE 가 데카르트 곱으로 생성하므로,
 * FE 는 **buildComboPreviewPairs 와 동일한 데카르트 곱 순서**로 각 조합의 (그룹순 선택지 라벨 + 초기/안전
 * 재고)를 배열로 싣는다. BE 는 생성 순서로 매칭한다(라벨도 함께 보내 순서 의존을 줄임). 입력이 하나도
 * 없으면(전 조합 미입력) undefined → 매퍼가 키를 생략(기존 동작: 조합 재고 0 기본).
 *
 * ★be2 CREATE 조합재고 수신 계약(ID-G-be.md) 확정 시 이 함수의 **출력 형태(키/필드명)만** 맞추면 된다
 *   — 조합 순서·재고 수집 로직은 불변(단일 swap 지점).
 */
const mapComboStocksPayload = (values: ProductFormValues) => {
  const groups = values.optionGroups ?? [];
  if (groups.length === 0) return undefined;
  const comboStocks = values.comboStocks ?? {};
  const combos = buildComboPreviewPairs(groups);
  if (combos.length === 0) return undefined;
  // 데카르트 곱 순서 보존 — BE 생성 순서와 정합. 라벨도 동봉(순서 의존 완화).
  const entries = combos.map((pairs) => {
    const stock = comboStocks[comboKeyOf(pairs)];
    return omitUndefined({
      // BE 매칭 보조 — 그룹순 선택지 라벨 배열(예: ["차콜","S"]).
      choiceLabels: pairs.map((p) => p.choiceLabel),
      initialStockQty: stock?.initialStockQty ?? undefined,
      safetyStockQty: stock?.safetyStockQty ?? undefined,
      // QA-585 — 등록 시 조합 옵션도 무한판매(재고추적 끔)로 만든다. 재고 관리는 등록 후 SKU 목록에서 켠다.
      stockTrackingEnabled: stock?.stockTrackingEnabled ?? false,
      weightGrams: stock?.weightGrams ?? undefined,
      // 자체 옵션코드 — 비면 미설정(생략).
      optionCode: stock?.optionCode?.trim() || undefined,
    });
  });
  // 전 조합이 빈 입력(재고 채널 0개)이면 전송 생략 — 기존 "조합 재고 미지정" 동작 보존.
  const hasAnyStock = entries.some((e) => Object.keys(e).length > 1); // choiceLabels 외 채널 존재.
  return hasAnyStock ? entries : undefined;
};

export const formToCreateRequest = (values: ProductFormValues): ProductCreateRequest => {
  // #10c 분리형 조합 옵션이 1개 이상이면 optionGroups 만 전송하고 평면 variants 는 보내지 않는다
  // (BE 계약: optionGroups 가 있으면 평면 variants 무시·혼용 금지, 그룹 데카르트 곱으로 조합 생성).
  const optionGroups = values.optionGroups ?? [];
  if (optionGroups.length > 0) {
    // QA-R6 G(옵션 A): 조합별 재고를 데카르트 곱 순서대로 동봉. be2 CREATE 수신 계약(ID-G-be.md)
    // 확정 전까지 키명은 `comboStocks`(잠정) — 계약 확정 시 이 키와 mapComboStocksPayload 출력만 맞춘다.
    const comboStocks = mapComboStocksPayload(values);
    return {
      ...buildCommonPayload(values),
      shippingFeeType: values.shippingFeeType ?? 'FREE',
      optionGroups: mapOptionGroupsPayload(optionGroups),
      ...(comboStocks ? { comboStocks } : {}),
    } as unknown as ProductCreateRequest;
  }
  // Task#4 — 옵션은 선택사항: 행이 0개면 variants 키 자체를 생략한다(BE #10a createVariants 가
  // 기본 옵션 1개를 자동 생성하는 무옵션 상품 경로).
  return omitUndefined({
    ...buildCommonPayload(values),
    shippingFeeType: values.shippingFeeType ?? 'FREE',
    variants:
      values.variants.length > 0
        ? values.variants.map((v) =>
            omitUndefined({
              variantCode: v.variantCode ?? undefined,
              label: variantLabelOrDefault(v.label, values.variants.length),
              additionalAmount: v.additionalAmount ?? undefined,
              initialStockQty: v.initialStockQty ?? undefined,
              safetyStockQty: v.safetyStockQty ?? undefined,
              // TASK-2 재고추적 — per-variant(VariantCreateDto.stockTrackingEnabled). 생성 시 null=BE 기본 true.
              stockTrackingEnabled: v.stockTrackingEnabled ?? undefined,
              // 결정① — 옵션 실측 무게(g). 생성 시 null=미설정(상품 무게 폴백)이라 생략.
              weightGrams: v.weightGrams ?? undefined,
              // 자체 옵션코드 — 생성 시 비면 미설정(생략).
              optionCode: v.optionCode?.trim() || undefined,
            }),
          )
        : undefined,
  }) as ProductCreateRequest;
};

/**
 * 편집 시 옵션/재고 페이로드. BE 는 detail.variant.stockQty 를 상품 총재고(StockEntry 합)로
 * 모든 variant 에 동일 노출하므로, 미변경 variant 에 그 값을 그대로 보내면 PUT 이 각 variant 재고를
 * total 로 절대설정 → 합산이 부풀어 오른다. 따라서 stockQty 가 원본(baseline)과 다른 variant 에서만
 * initialStockQty 를 전송하고(절대설정), 미변경 variant 는 variantCode+additionalAmount 만 round-trip.
 */
const buildUpdateVariants = (
  values: ProductFormValues,
  baselineStockByCode: Map<string, number>,
  baselineWeightByCode: Map<string, number>,
  baselineOptionCodeByCode: Map<string, string> = new Map(),
) =>
  values.variants.map((v) => {
    const code = v.variantCode ?? undefined;
    const baseline = code != null ? baselineStockByCode.get(code) : undefined;
    const stockChanged =
      v.initialStockQty != null && (baseline === undefined || v.initialStockQty !== baseline);
    // 결정① — 옵션 무게 편집 sync: 값 있으면 그대로, 로드된 무게를 비웠으면 0(=미설정 clear),
    // 원래 없던 무게가 빈 상태면 생략(null=미변경 보존 — 가드 1 동일 원칙).
    const weightBaseline = code != null ? baselineWeightByCode.get(code) : undefined;
    const weightGrams =
      v.weightGrams != null
        ? v.weightGrams
        : weightBaseline != null && weightBaseline > 0
          ? 0
          : undefined;
    // 자체 옵션코드 편집 sync(평면·조합 공통 — 조합은 variantCode 매칭 재고 패치 채널): 값 있으면 그대로,
    // 로드된 코드를 비웠으면 ""(=지움), 원래 없던 코드가 빈 상태면 생략(null=미변경).
    const optionCodeNow = v.optionCode?.trim() ?? '';
    const optionCodeBaseline = code != null ? baselineOptionCodeByCode.get(code) : undefined;
    const optionCode = optionCodeNow !== '' ? optionCodeNow : optionCodeBaseline ? '' : undefined;
    return omitUndefined({
      variantCode: code,
      label: variantLabelOrDefault(v.label, values.variants.length),
      additionalAmount: v.additionalAmount ?? undefined,
      initialStockQty: stockChanged ? v.initialStockQty : undefined,
      safetyStockQty: v.safetyStockQty ?? undefined,
      // TASK-2 재고추적 — per-variant. null=미변경(기존값 보존). 토글값 있으면 그대로 전송.
      stockTrackingEnabled: v.stockTrackingEnabled ?? undefined,
      weightGrams,
      optionCode,
    });
  });

/** 텍스트 baseline/현재값 존재 판정 — 공백뿐인 값은 빈 것으로 본다. */
const hasText = (v: string | null | undefined): boolean => Boolean(v && v.trim().length > 0);

/** 맵 baseline 존재 판정. */
const hasEntries = (m: Record<string, string> | null | undefined): boolean =>
  Boolean(m && Object.keys(m).length > 0);

/**
 * per-locale clear 판정 대상 필드(모두 BO 폼에 실제 비우기 경로가 있는 입력).
 * ★per-locale 텍스트는 ""(blank) 를 보내지 않는다 — SF 읽기 폴백이 null 체크라
 * (ProductDetailAssembler.applyProductTranslation:291,301-304) "" 는 PDP 에 빈 섹션을
 * 만든다. 대신 OMISSION-clear: BE upsert 는 포함된 locale entry 를 전체 교체
 * (update+updateExtended raw-assign, null=비움 — ProductTranslation.java:100-110,
 * CatalogProductWriter.upsertProductTranslations:931-948)하므로 entry 에서 필드를
 * 생략하면 NULL 로 비워져 SF 폴백(기본문/ko)이 정상 동작한다.
 */
const TRANSLATION_CLEARABLE_FIELDS = [
  'name',
  'description',
  'brandName',
  'eventPromoSubtitle',
  'detailHtml',
  'noticeText',
  'policyText',
  'shippingPolicyText',
  'noticeSpec',
] as const;

/** baseline locale entry 에 clear 대상 값이 하나라도 있었는지(빈 entry 주입 필요 판정). */
const baselineHasClearableValue = (entry: Record<string, unknown> | undefined): boolean => {
  if (!entry) return false;
  for (const fieldName of TRANSLATION_CLEARABLE_FIELDS) {
    const v = entry[fieldName];
    if (fieldName === 'noticeSpec') {
      if (hasEntries(v as Record<string, string> | null | undefined)) return true;
    } else if (hasText(v as string | null | undefined)) {
      return true;
    }
  }
  return false;
};

/**
 * [버그수정 — Lead 승인 + per-locale 시맨틱 보정] 명시적 clear. BE PUT 의 TOP-LEVEL 시맨틱은
 * per-field null=PRESERVE / ""=CLEAR(맵·배열은 전송 시 전체 교체, null 시 미변경)인데, BO 의
 * omit-on-empty 관례 때문에 "비우고 저장"이 조용한 no-op(리로드 시 부활)이었다.
 *
 * 가드 1 — baseline 인지: 편집 원본(original)에서 실제 값이 로드됐던 필드가 지금 비워진
 * 경우에만 clear 를 전송한다. 원래 비어 있던/미로드 필드는 절대 clear 로 바꾸지 않는다
 * (부분 폼 오삭제 방지). 등록(create)은 baseline 이 없어 자연 차단. per-locale baseline 은
 * translationsFromDetail(original) — 폼이 로드한 값과 동일 소스(레거시 top-level→ko 시드 포함).
 *
 * 가드 2 — D1 미러 불변: ko 정본 필드의 clear 는 top-level(""/{})과 translations.ko-KR
 * (omission→BE null) 양쪽에서 함께 비워져 두 컬럼이 발산하지 않는다(SF ko 는 top-level 만 읽음).
 *
 * per-locale 규칙(SF 폴백 보존): 포함된 locale entry 는 BE 전체 교체라 비워진 필드를 그냥
 * 생략(omission)하면 NULL 로 비워진다 — "" 주입 금지(빈 섹션 회귀). 단, locale entry 전체가
 * 비워져 cleanTranslations 가 locale 을 누락시키면 BE 가 그 row 를 건너뛰어 부활하므로,
 * baseline 에 값이 있던 locale 은 빈 entry {} 를 주입해 row 를 NULL 로 전체 교체시킨다
 * (옵션/variant 라벨 번역 upsert 는 null/빈 → no-op 이라 안전 — upsertVariantTranslations:958).
 */
const withExplicitClears = (
  payload: Record<string, unknown>,
  original: ProductDetail,
): Record<string, unknown> => {
  const base = translationsFromDetail(original);
  const d = original as unknown as {
    noticeSpec?: Record<string, string> | null;
    thumbnailUrl?: string | null;
    detailImages?: string[] | null;
    commerceMeta?: {
      seoTitle?: string | null;
      seoDescription?: string | null;
      weightGrams?: number | null;
    } | null;
  };
  const out: Record<string, unknown> = { ...payload };

  // 1) translations — 포함된 entry 의 비워진 필드는 omission 그대로 두고(BE 전체 교체가 NULL 처리),
  //    "통째로 빠진 locale + baseline 값 존재" 에만 빈 entry 를 주입해 row 가 스킵-부활하지 않게 한다.
  const payloadTranslations = (out.translations ?? {}) as Record<string, Record<string, unknown>>;
  const nextTranslations: Record<string, Record<string, unknown>> = { ...payloadTranslations };
  for (const loc of PRODUCT_LOCALES) {
    if (nextTranslations[loc]) continue; // 포함된 entry — BE 전체 교체가 omission 필드를 NULL 처리.
    const baseEntry = base?.[loc] as Record<string, unknown> | undefined;
    if (baselineHasClearableValue(baseEntry)) {
      nextTranslations[loc] = {};
    }
  }
  if (Object.keys(nextTranslations).length > 0) out.translations = nextTranslations;

  // 2) top-level — ko 정본 미러 필드 + 단독 필드. baseline 소스는 폼 로드 경로와 동일.
  const koBase = base?.['ko-KR'];
  if (!('detailHtml' in out) && hasText(koBase?.detailHtml)) out.detailHtml = '';
  if (!('eventPromoSubtitle' in out) && hasText(koBase?.eventPromoSubtitle)) {
    out.eventPromoSubtitle = '';
  }
  if (!('nameEn' in out) && hasText(base?.['en-US']?.name)) out.nameEn = '';
  // noticeSpec baseline = §11 편집기 시드 소스와 동일(top-level ?? 레거시 ko 번역).
  if (!('noticeSpec' in out) && (hasEntries(d.noticeSpec) || hasEntries(koBase?.noticeSpec))) {
    out.noticeSpec = {};
  }
  if (!('seoTitle' in out) && hasText(d.commerceMeta?.seoTitle)) out.seoTitle = '';
  if (!('seoDescription' in out) && hasText(d.commerceMeta?.seoDescription)) {
    out.seoDescription = '';
  }
  if (!('thumbnailUrl' in out) && hasText(d.thumbnailUrl)) out.thumbnailUrl = '';
  // 배열은 빈 배열 전송 = 전체 삭제(BE detailImages 계약: 전송 시 전체 교체, null 시 미변경).
  if (!('detailImages' in out) && (d.detailImages?.length ?? 0) > 0) out.detailImages = [];
  // ① 무게는 숫자 clear 시맨틱 — BE weightGrams: 0 = 미설정으로 clear(레거시 가정무게 복귀).
  if (!('weightGrams' in out) && (d.commerceMeta?.weightGrams ?? 0) > 0) out.weightGrams = 0;
  return out;
};

export const formToUpdateRequest = (
  values: ProductFormValues,
  original?: ProductDetail,
): ProductUpdateRequest => {
  const baselineStockByCode = new Map<string, number>();
  const baselineWeightByCode = new Map<string, number>();
  const baselineOptionCodeByCode = new Map<string, string>();
  for (const v of original?.variants ?? []) {
    if (v.variantCode != null && v.stockQty != null) {
      baselineStockByCode.set(v.variantCode, v.stockQty);
    }
    const weight = (v as unknown as { weightGrams?: number | null }).weightGrams;
    if (v.variantCode != null && weight != null) {
      baselineWeightByCode.set(v.variantCode, weight);
    }
    const optionCode = (v as unknown as { optionCode?: string | null }).optionCode;
    if (v.variantCode != null && optionCode != null && optionCode.trim() !== '') {
      baselineOptionCodeByCode.set(v.variantCode, optionCode);
    }
  }
  // Task#4 — 행이 0개면 variants 를 생략(옵션 무변경 시맨틱). 조회는 항상 ≥1 variant(기본 옵션
  // 자동 생성)를 내려주므로 정상 편집 흐름에서는 도달하지 않는 방어 경로다.
  const payload = omitUndefined({
    ...buildCommonPayload(values),
    // PUT 시 항상 전송 — null 은 reset 안 됨 (BE contract)
    shippingFeeType: values.shippingFeeType ?? 'FREE',
    variants:
      values.variants.length > 0
        ? buildUpdateVariants(values, baselineStockByCode, baselineWeightByCode, baselineOptionCodeByCode)
        : undefined,
    // 결정④ — 조합 옵션 reconcile(ProductUpdateRequest.optionGroups): 필드 생략=미변경(기존
    // 그룹/조합 보존), []=그룹 전체 제거(variant/재고 보존, 평면 경로 복귀), 1개 이상=reconcile
    // (id 있으면 ID-우선 매칭=rename-safe, 없으면 이름/라벨 매칭. 잔존=재고 유지·갱신, 신규=생성,
    // 제거=비활성). 그룹 경로일 땐 BE 가 평면 variants 입력을 무시한다(혼용 금지).
    optionGroups: values.optionGroups ? mapOptionGroupsPayload(values.optionGroups) : undefined,
  }) as Record<string, unknown>;
  // V130 수동 품절 — 체크가 바뀌었을 때만 보낸다(null/미전송 = 미변경). BE 는 false 를 받으면 재고 정합을 다시
  // 돌리므로(재고 있으면 SELLING 복귀) 매 저장마다 같은 값을 보내지 않는다.
  const loadedManualSoldOut = (original as { commerceMeta?: { manualSoldOut?: boolean | null } | null } | undefined)
    ?.commerceMeta?.manualSoldOut;
  if (
    typeof values.manualSoldOut === 'boolean' &&
    typeof loadedManualSoldOut === 'boolean' &&
    values.manualSoldOut !== loadedManualSoldOut
  ) {
    payload.manualSoldOut = values.manualSoldOut;
  }
  // 명시적 clear 는 편집 원본이 있을 때만(가드 1 — create/원본 미제공 경로는 기존 omit 유지).
  return (original ? withExplicitClears(payload, original) : payload) as ProductUpdateRequest;
};

/**
 * BE Instant(ISO, 'Z') → `<input type="datetime-local">` 표시값('YYYY-MM-DDTHH:mm', 로컬시각).
 * 빈/파싱불가 시 null. (datetime-local 은 'Z' 가 붙은 값을 표시하지 못해 편집 시 빈칸이 되던 문제 해소.)
 */
const instantToLocalInput = (v: string | null | undefined): string | null => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * 편집 로드 시 가격 맵 복원. KRW 기준가는 detail.listPrice/salePrice 가 정본(products 컬럼).
 * override-only 모델에서 BE 는 prices.KRW 를 null 로 내려주므로(예약 통화), KRW amount 가
 * 없으면 listPrice/salePrice 로 폴백해 ₩0 표시를 막는다.
 * 외화(USD/JPY/CNY)는 선택적 오버라이드라, BE 가 amount 를 내려준 경우에만 고정값으로
 * 복원하고 그 외엔 null(자동계산) 로 둔다.
 */
const pricesFromDetail = (detail: ProductDetail): CurrencyPrices => {
  const src = (detail as unknown as {
    prices?: Record<string, { listAmount?: number | string | null; saleAmount?: number | string | null }> | null;
  }).prices;
  const num = (v: number | string | null | undefined): number => {
    const n = typeof v === 'string' ? Number(v) : (v ?? 0);
    return Number.isFinite(n) ? (n as number) : 0;
  };
  const nullableNum = (v: number | string | null | undefined): number | null => {
    if (v == null || v === '') return null;
    const n = typeof v === 'string' ? Number(v) : v;
    return Number.isFinite(n) ? (n as number) : null;
  };
  const overridePick = (cur: string): { listAmount: number | null; saleAmount: number | null } => {
    const e = src?.[cur];
    if (!e) return { listAmount: null, saleAmount: null };
    return { listAmount: nullableNum(e.listAmount), saleAmount: nullableNum(e.saleAmount) };
  };
  // KRW: prices.KRW amount 가 null 이면(override-only) listPrice/salePrice 로 폴백.
  // 정가(listPrice)마저 없으면 판매가를 정가로 쓴다 — 아래 listPrice 폴백과 같은 이유(이관 상품).
  const krwEntry = src?.KRW;
  const krwList =
    nullableNum(krwEntry?.listAmount) ??
    nullableNum(detail.listPrice?.amount) ??
    num(detail.salePrice?.amount);
  const krwSale = nullableNum(krwEntry?.saleAmount) ?? num(detail.salePrice?.amount);
  return {
    KRW: { listAmount: krwList, saleAmount: krwSale },
    USD: overridePick('USD'),
    JPY: overridePick('JPY'),
    CNY: overridePick('CNY'),
  };
};

const emptyTranslationEntry = () => ({
  name: '',
  description: '',
  brandName: '',
  eventPromoSubtitle: '',
  detailHtml: '',
  noticeText: '',
  policyText: '',
  shippingPolicyText: '',
  noticeSpec: {} as Record<string, string>,
});

const translationsFromDetail = (detail: ProductDetail): ProductTranslations => {
  const result: ProductTranslations = {
    'ko-KR': emptyTranslationEntry(),
    'en-US': emptyTranslationEntry(),
    'ja-JP': emptyTranslationEntry(),
    'zh-CN': emptyTranslationEntry(),
  };
  const src = (detail as unknown as {
    translations?: Record<
      string,
      {
        name?: string | null;
        description?: string | null;
        brandName?: string | null;
        eventPromoSubtitle?: string | null;
        detailHtml?: string | null;
        noticeText?: string | null;
        policyText?: string | null;
        shippingPolicyText?: string | null;
        noticeSpec?: Record<string, string> | null;
        options?: Array<{ optionId?: string | null; name?: string | null; choices?: Array<{ choiceId?: string | null; label?: string | null }> | null }> | null;
        variants?: Array<{ variantCode?: string | null; label?: string | null }> | null;
      }
    >;
  }).translations;
  if (src) {
    for (const loc of PRODUCT_LOCALES) {
      const entry = src[loc];
      if (entry && result) {
        const options = (entry.options ?? [])
          .filter((o): o is { optionId: string; name?: string | null; choices?: Array<{ choiceId?: string | null; label?: string | null }> | null } => Boolean(o.optionId))
          .map((o) => ({
            optionId: o.optionId,
            name: o.name ?? '',
            ...(o.choices && o.choices.length > 0
              ? {
                  choices: o.choices
                    .filter((c): c is { choiceId: string; label?: string | null } => Boolean(c.choiceId))
                    .map((c) => ({ choiceId: c.choiceId, label: c.label ?? '' })),
                }
              : {}),
          }));
        // ID42 #14: variant 라벨 번역 복원 — variantCode 있는 행만, label null→'' (미입력).
        const variants = (entry.variants ?? [])
          .filter((v): v is { variantCode: string; label?: string | null } => Boolean(v.variantCode))
          .map((v) => ({ variantCode: v.variantCode, label: v.label ?? '' }));
        result[loc] = {
          name: entry.name ?? '',
          description: entry.description ?? '',
          brandName: entry.brandName ?? '',
          eventPromoSubtitle: entry.eventPromoSubtitle ?? '',
          detailHtml: entry.detailHtml ?? '',
          noticeText: entry.noticeText ?? '',
          policyText: entry.policyText ?? '',
          shippingPolicyText: entry.shippingPolicyText ?? '',
          noticeSpec: entry.noticeSpec ?? {},
          ...(options.length > 0 ? { options } : {}),
          ...(variants.length > 0 ? { variants } : {}),
        };
      }
    }
  }
  // localizedNames fallback (translations 미제공 시)
  const localized = detail.localizedNames as Record<string, string> | undefined;
  if (localized && result) {
    for (const loc of PRODUCT_LOCALES) {
      const localName = localized[loc];
      if (localName && result[loc] && !result[loc]!.name) {
        result[loc] = { ...result[loc]!, name: localName };
      }
    }
  }
  // 정본 일원화(편집 로드) — ko 번역에 detailHtml 행이 없고 top-level products.detail_html(레거시)만
  // 있는 상품은, ko 정본 에디터가 비어 보이면 기존 본문이 사라진다. ko detailHtml 이 비어 있으면
  // top-level detail.detailHtml 로 시드해 무손실 round-trip 을 보장한다(§5 에디터 제거 회귀 방지).
  const topLevelDetailHtml = (detail as unknown as { detailHtml?: string | null }).detailHtml;
  if (result['ko-KR'] && !result['ko-KR']!.detailHtml?.trim() && topLevelDetailHtml?.trim()) {
    result['ko-KR'] = { ...result['ko-KR']!, detailHtml: topLevelDetailHtml };
  }
  // D5 — 이벤트 부제도 동일 패턴: ko 번역이 비어 있고 레거시 top-level 만 있으면 ko 로 시드해야
  // 입력란(§2 ko 탭)에 보이고, 저장 시 top-level 도출(미러)이 값을 보존한다.
  const topLevelSubtitle = (detail as unknown as { eventPromoSubtitle?: string | null }).eventPromoSubtitle;
  if (result['ko-KR'] && !result['ko-KR']!.eventPromoSubtitle?.trim() && topLevelSubtitle?.trim()) {
    result['ko-KR'] = { ...result['ko-KR']!, eventPromoSubtitle: topLevelSubtitle };
  }
  return result;
};

/**
 * D1 — §11 고시 항목 편집 로드 시드. top-level products.notice_spec 이 정본이지만, 레거시 상품이
 * §2 ko 탭(구 중복 입력)으로만 translations['ko-KR'].noticeSpec 을 채워둔 경우 top-level 이 비어
 * 있을 수 있다. 그땐 ko 번역 값을 §11 편집기로 시드해 무손실 round-trip(저장 시 양쪽 동기화)을 보장한다.
 */
const noticeSpecFromDetail = (
  topLevel: Record<string, string> | null | undefined,
  translations: ProductTranslations,
): Record<string, string> => {
  if (topLevel && Object.keys(topLevel).length > 0) return topLevel;
  const koLegacy = translations?.['ko-KR']?.noticeSpec;
  if (koLegacy && Object.keys(koLegacy).length > 0) return { ...koLegacy };
  return {};
};

/** 옵션 없이 등록한 상품의 기본 옵션명 — 이관 보정값과 같다. */
export const DEFAULT_VARIANT_LABEL = '기본';

/** QA-521 — 옵션이 1개뿐이고 라벨을 비웠으면 기본 라벨("기본")로 보낸다. 2개 이상은 폼 검증이 막는다. */
export const variantLabelOrDefault = (label: string, variantCount: number): string =>
  label.trim() || (variantCount <= 1 ? DEFAULT_VARIANT_LABEL : label);

/** 상세 응답의 옵션명(catalog V135) — generated 타입 반영 전이라 안전 캐스팅. 없으면 null. */
export const readOptionName = (detail: unknown): string | null =>
  (detail as { optionName?: string | null } | null | undefined)?.optionName ?? null;

export const detailToFormValues = (detail: ProductDetail): ProductFormValues => {
  const d = detail as unknown as {
    detailHtml?: string | null;
    detailImages?: string[] | null;
    thumbnailUrl?: string | null;
    noticeSpec?: Record<string, string> | null;
    shippingFree?: boolean | null;
    wishlistEnabled?: boolean | null;
    displayed?: boolean | null;
    // F-1 B2B 전용 — BE 응답 위치(top-level vs commerceMeta) 무관하게 복원하려 둘 다 읽는다.
    b2bOnly?: boolean | null;
    purchaseLimitMin?: number | null;
    purchaseLimitMax?: number | null;
    purchaseLimitBasis?: 'OPTION' | 'PRODUCT' | 'MEMBER' | null;
    eventPromoSubtitle?: string | null;
    stockInboundDate?: string | null;
    shippingStartAt?: string | null;
    brandId?: string | null;
    // BE 가 ProductDetailView 에 노출(generated 타입 미반영 → 안전캐스트). null = 미선택.
    primaryCategoryId?: string | null;
    shipping?: ShippingWire | null;
    policyLinks?: PolicyLinksWire | null;
    // R5 A-2 — 응답은 단일 trailing 객체 commerceMeta 로 묶여 옴.
    commerceMeta?: CommerceMetaWire | null;
  };
  const shipping = d.shipping ?? undefined;
  const policyLinks = d.policyLinks ?? undefined;
  const meta = d.commerceMeta ?? undefined;
  const translations = translationsFromDetail(detail);
  return {
    productType: detail.productType,
    // QA-606 — BE 는 비공개(HIDDEN)를 판매 상태가 아닌 노출 끔(displayed=false)으로만 저장하고, 상세 status 는 판매 축(판매중 등)을
    //   돌려준다. 그대로 두면 비공개로 저장한 상품이 다시 열면 '판매중'으로 보였다 → 노출이 꺼져 있으면 [비공개]로 보인다.
    //   저장 때 HIDDEN 은 판매 상태를 바꾸지 않아(BE toCanonical=null) 원래 판매 상태는 그대로 남는다.
    status: d.displayed === false ? 'HIDDEN' : detail.status,
    // V130 수동 품절 — 응답에 boolean 이 있을 때만 폼에 싣는다(없으면 체크박스를 숨겨 저장해도 무시되는 일이 없게).
    ...(typeof meta?.manualSoldOut === 'boolean' ? { manualSoldOut: meta.manualSoldOut } : {}),
    // 노출여부 — BE 가 명시 false 일 때만 OFF, 그 외(미제공/true)는 ON 으로 복원.
    displayed: d.displayed ?? true,
    // F-1 B2B 전용 — top-level 우선, 없으면 commerceMeta 폴백, 둘 다 없으면 false(기본).
    b2bOnly: d.b2bOnly ?? meta?.b2bOnly ?? false,
    purchasePermission: toMemberPermission(meta?.purchasePermission),
    purchaseGradeIds: meta?.purchaseGradeIds ?? [],
    purchaseDeniedText: meta?.purchaseDeniedText ?? '',
    purchaseDeniedTextEnabled: (meta?.purchaseDeniedText ?? '').trim() !== '',
    accessPermission: toMemberPermission(meta?.accessPermission),
    accessGradeIds: meta?.accessGradeIds ?? [],
    accessDeniedDisplay: meta?.accessDeniedDisplay ?? true,
    // QA-456 2단계 마일리지 지급 대상 — "NONE" 이 있으면 지급 안 함, 등급이 있으면 특정회원등급, 비면 전체회원.
    ...(Array.isArray(meta?.mileageGradeIds)
      ? {
          mileageTarget: meta.mileageGradeIds.includes(MILEAGE_NONE)
            ? ('NONE' as const)
            : meta.mileageGradeIds.length > 0
              ? ('GRADE' as const)
              : ('ALL' as const),
          mileageGradeIds: meta.mileageGradeIds.filter((id) => id !== MILEAGE_NONE),
        }
      : {}),
    // QA-579 마일리지 사용 제외 — 응답에 필드가 있을 때만.
    ...(Array.isArray(meta?.mileageUseExclusionGradeIds)
      ? {
          mileageUseExclusionTarget: meta.mileageUseExclusionGradeIds.includes(MILEAGE_USE_EXCLUDE_ALL)
            ? ('ALL' as const)
            : meta.mileageUseExclusionGradeIds.length > 0
              ? ('GRADE' as const)
              : ('NONE' as const),
          mileageUseExclusionGradeIds: meta.mileageUseExclusionGradeIds.filter((id) => id !== MILEAGE_USE_EXCLUDE_ALL),
        }
      : { mileageUseExclusionTarget: undefined }),
    // QA-456 혜택 제외 — 응답에 필드가 있을 때만 폼에 싣는다(없으면 저장 때 보내지 않음). 대상 등급이 있으면 특정회원등급.
    ...(Array.isArray(meta?.benefitExclusions)
      ? {
          benefitExclusions: meta.benefitExclusions.filter((v): v is BenefitExclusion => BENEFIT_EXCLUSIONS.includes(v as BenefitExclusion)),
          benefitExclusionTarget: (meta.benefitExclusionGradeIds?.length ?? 0) > 0 ? ('GRADE' as const) : ('ALL' as const),
          benefitExclusionGradeIds: meta.benefitExclusionGradeIds ?? [],
        }
      : {}),
    // mall-visibility — 정본 commerceMeta.visibleMallCodes(be-cat V116, displayStartAt/weightGrams 위치).
    // 응답엔 top-level 노출 없음(항상 commerceMeta). 없음/null/빈/4몰 = 전체노출(전 몰 선택)로 복원.
    visibleMalls: visibleMallsFromDetail(meta?.visibleMallCodes),
    // QA-507 공통 동의 사용 코드 — 이관 상품은 고도몰 사용함 값(eventopt3~6).
    commonConsentCodes: commonConsentCodesFromDetail(meta?.commonConsentCodes),
    // ID39 노출 예약 window — 응답은 commerceMeta trailing. Instant → datetime-local 표시값.
    displayStartAt: instantToLocalInput(meta?.displayStartAt),
    displayEndAt: instantToLocalInput(meta?.displayEndAt),
    primaryCategoryId: d.primaryCategoryId ?? null,
    brandId: d.brandId ?? null,
    nameKo: detail.localizedNames?.['ko-KR'] ?? detail.name,
    nameEn: detail.localizedNames?.['en-US'] ?? null,
    artistName: detail.artistName ?? null,
    artistId: detail.artistId ?? null,
    artistMemberIds:
      detail.artistMembers && detail.artistMembers.length > 0
        ? detail.artistMembers.map((m) => m.memberArtistId)
        : null,
    // QA-231 — productCode 는 서버 자동배정 고유값(read-only 표시용). 레거시 null 은 빈 문자열.
    productCode: detail.productCode ?? '',
    // QA-231 자체상품코드(앨범별 고정 코드) round-trip. 미설정이면 빈 문자열.
    selfProductCode: detail.selfProductCode ?? '',
    barcode: detail.barcode ?? null,
    optionName: readOptionName(detail) ?? '',
    originCountry: detail.originCountry ?? null,
    manufacturerName: detail.manufacturerName ?? null,
    manufacturedYearMonth: detail.manufacturedYearMonth ?? null,
    translations,
    eventPromoSubtitle: d.eventPromoSubtitle ?? null,
    // 이관(고도몰) 상품은 대부분 정가가 비어 있다(로컬 이관본 24,696건 중 22,071건). 그대로 두면
    // ① 폼 스키마가 listPrice 를 객체로 요구해 **화면에 입력칸이 없는 필드에서** 검증이 조용히 실패하고,
    // ② 정가가 0 으로 채워져 "판매가 ≤ 정가" 검사도 걸린다 — 저장 버튼이 아무 반응 없는 상태가 된다.
    // BE 수정 API 도 listPrice 를 @NotNull 로 받으므로 값을 보내야 한다. 정가 없음 = 할인 없음이라
    // 판매가를 정가로 쓴다(쇼핑몰 표시도 할인 없음으로 같다).
    listPrice: detail.listPrice ?? detail.salePrice,
    salePrice: detail.salePrice,
    prices: pricesFromDetail(detail),
    salesStartAt: instantToLocalInput(detail.salesStartAt),
    salesEndAt: instantToLocalInput(detail.salesEndAt),
    releaseDate: detail.releaseDate ?? null,
    stockInboundDate: d.stockInboundDate ?? null,
    shippingStartAt: instantToLocalInput(d.shippingStartAt),
    purchaseLimitMin: d.purchaseLimitMin ?? null,
    purchaseLimitMax: d.purchaseLimitMax ?? null,
    // QA-542 이관 상품은 고도몰 설정값(상품·아이디 기준)이 들어 있다. 없으면 옵션 기준.
    purchaseLimitBasis: d.purchaseLimitBasis ?? 'OPTION',
    variants: detail.variants.length
      ? detail.variants.map((v) => {
          const vv = v as unknown as {
            safetyStockQty?: number | null;
            variantId?: string | null;
            stockTrackingEnabled?: boolean | null;
            weightGrams?: number | null;
            optionCode?: string | null;
          };
          return {
            // ID42 옵션명 번역은 translations.{locale}.options[].optionId = variantId 로 상관.
            variantId: vv.variantId ?? null,
            variantCode: v.variantCode ?? null,
            // 이관된 단일 옵션 상품의 기본 옵션은 label 이 null 이다(이관 보정 전 17,438건). 폼은 옵션명을
            // 필수 문자열로 요구해 "SKU List: Expected string, received null" 로 저장이 막힌다. 옵션이
            // 하나뿐이면 에버라인이 옵션 없이 등록한 상품과 같은 기본 옵션명 '기본' 을 채운다(이관팀 보정값과
            // 동일). 옵션이 여럿인데 이름이 비어 있으면 '' 로 두어 폼이 "옵션명 필수" 를 보여주게 한다 —
            // 어느 옵션인지 모르는 채로 이름을 지어내지 않는다.
            label: v.label?.trim() || (detail.variants.length === 1 ? DEFAULT_VARIANT_LABEL : ''),
            additionalAmount: v.additionalPrice?.amount ?? null,
            initialStockQty: v.stockQty ?? null,
            safetyStockQty: vv.safetyStockQty ?? null,
            // TASK-2 재고추적 — per-variant. 미제공(레거시) 시 true(ON) 복원(BE 기본값 정합).
            stockTrackingEnabled: vv.stockTrackingEnabled ?? true,
            // 결정① — 옵션 무게(g) round-trip(VariantView.weightGrams). null=미설정(상품 무게 폴백).
            weightGrams: vv.weightGrams ?? null,
            // 자체 옵션코드(VariantView.optionCode) round-trip. null=미설정.
            optionCode: vv.optionCode ?? null,
          };
        })
      : // Task#4 — variant 없는 상품은 빈 배열로 복원(옵션 선택사항). 빈 라벨 placeholder 행을
        // 만들면 라벨 필수 검증이 저장을 막는다.
        [],
    thumbnailUrl: d.thumbnailUrl ?? null,
    detailImages: d.detailImages ?? [],
    detailHtml: d.detailHtml ?? null,
    shippingFree: d.shippingFree ?? false,
    wishlistEnabled: d.wishlistEnabled ?? true,
    shippingFeeType: toShippingFeeType(shipping?.feeType) ?? 'FREE',
    baseFee: shipping?.baseFee ?? null,
    islandExtraFee: shipping?.islandExtraFee ?? null,
    freeThreshold: shipping?.freeThreshold ?? null,
    returnFee: shipping?.returnFee ?? null,
    exchangeFee: shipping?.exchangeFee ?? null,
    // M-8 ID81 배송비 마스터 연결 — CommerceMetaView round-trip(meta.shippingFeePolicyId).
    shippingFeePolicyId: meta?.shippingFeePolicyId ?? null,
    // ① Task#7 — 실측 무게(g) round-trip(meta.weightGrams). null = 미설정.
    weightGrams: meta?.weightGrams ?? null,
    shippingPolicyId: policyLinks?.shippingPolicyId ?? null,
    guidePolicyId: policyLinks?.guidePolicyId ?? null,
    operationPolicyId: policyLinks?.operationPolicyId ?? null,
    // D1 — §11 편집기 시드: top-level 정본 우선, 비어 있으면 레거시 ko 번역 noticeSpec 폴백(무손실).
    noticeSpec: noticeSpecFromDetail(d.noticeSpec, translations),
    // R5 A-2 commerceMeta reload (응답은 commerceMeta{}, 요청은 flat).
    additionalCategoryIds: meta?.additionalCategoryIds ?? undefined,
    taxExempt: meta?.taxExempt ?? false,
    searchKeywords: meta?.searchKeywords ?? null,
    sortWeight: meta?.sortWeight ?? null,
    seoTitle: meta?.seoTitle ?? null,
    seoDescription: meta?.seoDescription ?? null,
    relatedProductIds: meta?.relatedProductIds ?? null,
    presaleStatus: toPresaleStatus(meta?.presaleStatus),
  };
};
